import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Project } from '../../../types/Project';
import MainSectionComplete from '../MainSectionComplete/MainSectionComplete';
import MainSectionLiveVideo from '../MainSectionLiveVideo/MainSectionLiveVideo';
import { PopupModal } from '../../Modals/PopupModal/PopupModal';
import './MainSectionVideo.scss';
import { UserTypeEnum } from '../../../types/enums/UserTypesEnum';
import MainSectionPreviewVideo from '../MainSectionPreviewVideo/MainSectionPreviewVideo';
import useProject from '../../../hooks/useProject';
import { StatusEnum } from '../../../types/enums/StatusEnum';
import { useMutate } from 'restful-react';
import { CaptionModeEnum } from '../../../types/enums/CaptionModeEnum';
import { ClearTextModeEnum } from '../../../types/enums/ClearTextModeEnum';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import { Option } from '../../../common/interfaces';
import { Dropdown } from '../../FormComponents/Dropdown/Dropdown';
import { captionLanguages } from '../../../common/constants';

type MainSectionVideoProps = {
  isAwaitingApiSaveToComplete: boolean;
  userType: UserTypeEnum | undefined;
};

const MainSectionVideo = ({ isAwaitingApiSaveToComplete, userType }: MainSectionVideoProps) => {
  const { t } = useTranslation();
  const { project } = useProject();

  const [playerUserType, setPlayerUserType] = useState(UserTypeEnum.Captioner);
  const [playerIso, setPlayerIso] = useState(project?.encoder?.languageIsoCode);

  const [loadingBuffer, setLoadingBuffer] = useState(false);
  const [loadingOverlay, setLoadingOverlay] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showPaymentFailedModal, setShowPaymentFailedModal] = useState<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [closedPaymentFailedModal, setClosedPaymentFailedModal] = useState<boolean>(false);

  type ClearTextPayload = {
    mode: ClearTextModeEnum;
  };

  const getLanguageFromIso = (value: string): string => {
    const option = captionLanguages.find(opt => opt.value === value);
    return option ? option.text : 'Unknown language';
  };

  const loadStreamSources = () => {
    const options: Option[] = [];

    options.push({ text: 'Incoming source stream', value: 'source' });

    const encoders = project?.stream?.encoders;

    encoders?.forEach((enc) => {
      options.push({ text: `${getLanguageFromIso(enc.languageIsoCode)} destination stream`, value: enc.languageIsoCode });
    });

    return options;
  };

  const onChangeStreamSource = (option: Option) => {
    if (option.value === 'source') {
      setPlayerUserType(UserTypeEnum.Captioner);
      setPlayerIso(project?.encoder?.languageIsoCode);
    } else {
      setPlayerUserType(UserTypeEnum.Producer);
      setPlayerIso(option.value);
    }
  };

  const isPaymentFailed = useCallback(() => {
    if (project?.paymentStatus === 'requires_action') {
    }

    if (
      project?.paymentStatus === 'free' ||
      project?.paymentStatus === '' ||
      project?.paymentStatus === 'succeeded' ||
      !project?.paymentStatus
    ) {
      setShowPaymentFailedModal(false);
      return false;
    }

    setShowPaymentFailedModal(true);
    return true;
  }, [project]);

  useEffect(() => {
    isPaymentFailed();
  }, [project, isPaymentFailed]);

  const isCaptionerPreview = (
    project: Project | null,
    userType: UserTypeEnum | undefined,
  ): boolean => !project?.broadcastStartedUtc && userType === UserTypeEnum.Captioner;

  const isLiveAndCaptions = () => project?.broadcastStartedUtc && !project?.broadcastEndedUtc && project.encoder.captionMode !== CaptionModeEnum.None;

  const isOpen = (): boolean => project?.encoder.captionMode === CaptionModeEnum.Open;

  const onClearBufferTextSuccess = () => {
    setLoadingBuffer(false);
  };

  const onClearOverlayTextSuccess = () => {
    setLoadingOverlay(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { mutate: clearTextBufferRequest, loading: clearingBuffer, error: clearingBufferError } = useMutate<ClearTextPayload>({
    verb: 'POST',
    path: `/projects/${project?.id}/clear-text`,
    onMutate: onClearBufferTextSuccess,
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { mutate: clearTextOverlayRequest, loading: clearingOverlay, error: clearingOverlayError } = useMutate<ClearTextPayload>({
    verb: 'POST',
    path: `/projects/${project?.id}/clear-text`,
    onMutate: onClearOverlayTextSuccess,
  });

  const handleClearBufferText = (event?: React.BaseSyntheticEvent) => {
    event?.preventDefault();
    setLoadingBuffer(true);

    clearTextBufferRequest({
      mode: ClearTextModeEnum.Buffer
    });
  };

  const handleClearOverlayText = (event?: React.BaseSyntheticEvent) => {
    event?.preventDefault();

    setLoadingOverlay(true);

    clearTextOverlayRequest({
      mode: ClearTextModeEnum.Overlay
    });
  };

  return (
    <>
      <PopupModal
        isOpen={showPaymentFailedModal && !closedPaymentFailedModal}
        handleCloseModal={() => setClosedPaymentFailedModal(true)}
        title="producer.payment-failed.title"
        content={t('producer.payment-failed.content')}
      />
      <div className="main-section-video">
        <div className="main-section-container">
          {project?.broadcastEndedUtc || project?.deletedUtc || project.stream.status === StatusEnum.Stopped ? (
            <MainSectionComplete />
          ) : (
            <>
              {project?.broadcastStartedUtc ? (
                <MainSectionLiveVideo project={project} userType={playerUserType} streamIso={playerIso} />
              ) : (
                <div className={`main-section-container__iframe-overlay is-fhd`}>
                  <MainSectionPreviewVideo project={project as Project} />
                </div>
              )}
            </>
          )}
        </div>

        {isCaptionerPreview(project, userType) && (
          <div className="captioner-preview">{t('project-user.captioner.captioning-preview')}</div>
        )}

        {isLiveAndCaptions() && userType !== UserTypeEnum.Captioner && (
          <div className="player-source-select">
            <Dropdown
              options={loadStreamSources()}
              value='source'
              onChange={onChangeStreamSource}
              placeholderKey="producer.inputs.audio-device.placeholder"
              readonly={false}
            />
          </div>
        )}

        {isLiveAndCaptions() && (
          <div className="text-features">
            {userType !== UserTypeEnum.Captioner && (
              <button
                className="btn btn-primary btn-primary--pink"
                onClick={() => handleClearBufferText()}
                disabled={loadingBuffer}
              >
                {loadingBuffer ? (
                  <LoadingSpinner isButton={true} />
                ) : 
                (
                  t('project-user.captioner.clear-buffer-text')
                )}
              </button>
            )}

            {isOpen() && (
              <>
                <button
                  className="btn btn-primary btn-primary--pink"
                  onClick={() => handleClearOverlayText()}
                  disabled={loadingOverlay} 
                >
                  {loadingOverlay ? (
                    <LoadingSpinner isButton={true} />
                  ) : 
                  (
                    t('project-user.captioner.clear-overlay-text')
                  )}
                </button>
              </>
            )}
          </div>
        )}

        {isAwaitingApiSaveToComplete && (
          <p className="main-section-video__layout-updating-message">
            {t('producer.control-panel.layout-updating')}
          </p>
        )}
      </div>
    </>
  );
};

export default MainSectionVideo;
